import React, { useState, useEffect, useRef } from 'react';
import { FormInput } from '../FormInput/FormInput';
import { useToast } from '../Toast/ToastProvider';
import businessService from 'api/services/business';
import liquidTrustService from 'api/services/liquidtrust';

import './BusinessRegistrationForm.scss';

/**
 * Default business type options (fallback when API unavailable)
 */
const DEFAULT_BUSINESS_TYPE_OPTIONS = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llc', label: 'LLC (Limited Liability Company)' },
  { value: 'corporation', label: 'Corporation' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'nonprofit', label: 'Nonprofit Organization' },
  { value: 'other', label: 'Other' },
];

/**
 * Company size options
 */
const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

/**
 * Industry options
 */
const INDUSTRY_OPTIONS = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'entertainment', label: 'Entertainment & Media' },
  { value: 'other', label: 'Other' },
];

/**
 * Subtitle text based on context
 */
const SUBTITLE_TEXT = {
  business:
    'To create contracts on our platform, you need to register a business entity. This helps us verify your identity and ensure secure transactions.',
  contracts:
    'To accept contracts on our platform, you need to register your business entity. This helps us verify your identity and ensure secure payments.',
};

/**
 * BusinessRegistrationForm Component
 *
 * A form for users to register their business entity.
 * Required before creating contracts on the platform.
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback when business is successfully registered
 * @param {Function} props.onCancel - Callback when user cancels registration (optional)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.context - Context where form is displayed ('business' or 'contracts')
 */
const BusinessRegistrationForm = ({
  onSuccess,
  onCancel,
  className = '',
  context = 'business',
}) => {
  const { showError, showSuccess } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState(
    DEFAULT_BUSINESS_TYPE_OPTIONS
  );
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isLoadingBusinessTypes, setIsLoadingBusinessTypes] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Required fields
    name: '',
    businessType: 'sole_proprietorship',
    email: '',

    // Optional - Legal Entity
    legalName: '',
    taxId: '',
    registrationNumber: '',

    // Optional - Contact
    phone: '',
    website: '',

    // Optional - Address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',

    // Optional - Business Details
    industry: '',
    description: '',
    companySize: '',

    // LiquidTrust specific fields
    globalBusinessTypeId: null,
  });

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      const result = await liquidTrustService.getCountries();
      if (result.success && result.data) {
        const countryOptions = result.data.map((country) => ({
          value: country.country_alpha2_code || country.code,
          label: country.name,
        }));
        setCountries(countryOptions);
      }
      setIsLoadingCountries(false);
    };

    fetchCountries();
  }, []);

  // Fetch business types when selectedCountry changes
  useEffect(() => {
    // Skip if no country selected
    if (!selectedCountry) {
      setBusinessTypeOptions(DEFAULT_BUSINESS_TYPE_OPTIONS);
      return;
    }

    const fetchBusinessTypes = async () => {
      setIsLoadingBusinessTypes(true);
      const result = await liquidTrustService.getBusinessTypes(selectedCountry);

      if (result.success && result.data && result.data.length > 0) {
        const typeOptions = result.data.map((type) => ({
          value: (type.business_type_id || type.id).toString(),
          label: type.business_type_name || type.name,
        }));
        setBusinessTypeOptions(typeOptions);
      } else {
        // Fallback to default options if API fails
        setBusinessTypeOptions(DEFAULT_BUSINESS_TYPE_OPTIONS);
      }
      setIsLoadingBusinessTypes(false);
    };

    fetchBusinessTypes();
  }, [selectedCountry]);

  /**
   * Handle country selection separately to avoid triggering business type fetch loop
   */
  const handleCountryChange = (value) => {
    // Update both formData.country and selectedCountry
    setFormData((prev) => ({
      ...prev,
      country: value,
      // Reset business type when country changes
      businessType: '',
      globalBusinessTypeId: null,
    }));
    // This triggers the business types fetch
    setSelectedCountry(value);

    // Clear error for country field
    if (errors.country) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.country;
        return updated;
      });
    }
  };

  /**
   * Handle business type selection
   */
  const handleBusinessTypeChange = (value) => {
    const selectedType = businessTypeOptions.find((opt) => opt.value === value);
    setFormData((prev) => ({
      ...prev,
      businessType: selectedType ? selectedType.label : value,
      globalBusinessTypeId: selectedType
        ? parseInt(selectedType.value, 10)
        : null,
    }));

    // Clear error for businessType field
    if (errors.businessType) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.businessType;
        return updated;
      });
    }
  };

  /**
   * Update form field
   */
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Business name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Business name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Business email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required';
    }

    // Optional field validations
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website =
        'Website must be a valid URL (start with http:// or https://)';
    }

    if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number contains invalid characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await businessService.createBusiness(formData);

      if (result.success) {
        showSuccess('Business registered successfully!');

        // Call success callback with the created business
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(result.data);
          }, 1000);
        }
      } else {
        showError(result.error || 'Failed to register business');
      }
    } catch (error) {
      console.error('Error registering business:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`business-registration-form ${className}`}>
      <div className='business-registration-header'>
        <h2>Register Your Business</h2>
        <p className='subtitle'>
          {SUBTITLE_TEXT[context] || SUBTITLE_TEXT.business}
        </p>
      </div>

      <form onSubmit={handleSubmit} className='business-form'>
        {/* Required Information Section */}
        <div className='form-section'>
          <h3>Required Information</h3>
          <div className='form-grid'>
            <FormInput
              id='business-name'
              label='Business Name'
              value={formData.name}
              onChange={(value) => handleFieldChange('name', value)}
              placeholder='Enter your business name'
              required
              error={errors.name}
            />

            <FormInput
              id='country'
              label='Country'
              type='select'
              value={formData.country}
              onChange={handleCountryChange}
              options={countries}
              required
              error={errors.country}
              disabled={isLoadingCountries}
              placeholder={
                isLoadingCountries ? 'Loading countries...' : 'Select a country'
              }
            />

            <FormInput
              id='business-type'
              label='Business Type'
              type='select'
              value={formData.globalBusinessTypeId?.toString() || ''}
              onChange={handleBusinessTypeChange}
              options={businessTypeOptions}
              required
              error={errors.businessType}
              disabled={
                isLoadingCountries ||
                isLoadingBusinessTypes ||
                !formData.country
              }
              placeholder={
                isLoadingCountries || !formData.country
                  ? 'Select a country first'
                  : isLoadingBusinessTypes
                  ? 'Loading business types...'
                  : 'Select a business type'
              }
            />

            <FormInput
              id='business-email'
              label='Business Email'
              type='text'
              value={formData.email}
              onChange={(value) => handleFieldChange('email', value)}
              placeholder='contact@yourbusiness.com'
              required
              error={errors.email}
            />
          </div>
        </div>

        {/* TODO: Re-enable these optional sections when needed */}
        {/* Legal Entity Section - Hidden for now */}
        {/* Contact Information Section - Hidden for now */}
        {/* Address Section - Hidden for now */}
        {/* Business Details Section - Hidden for now */}

        {/* Form Actions */}
        <div className='form-actions'>
          {onCancel && (
            <button
              type='button'
              className='btn-secondary'
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button type='submit' className='btn-primary' disabled={isSubmitting}>
            {isSubmitting ? 'Registering...' : 'Register Business'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessRegistrationForm;
